import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './Projects.css';
import HighlightedTitle from '../components/HighlightedTitle';
import ProjectPage from '../components/ProjectPage';

function Animation() {
  const projects = React.useMemo(() => [
    {
      id: 'my-little-chum',
      title: 'My Little Chum',
      date: '2026',
      summary: 'An animated music video for Teddy Mailer\'s song \' My Little Chum \'.',
      images: [{ type: 'image', url: '/project_images_compressed/animation/my-little-chum/my-little-chum.jpg' }, { type: 'video', url: 'https://www.youtube.com/watch?v=4m71smAjAJ8' }],
    },
    {
      id: 'paper-plane',
      title: 'Paper Plane',
      date: '2025',
      summary: 'An animated short about childhood dreams.',
      images: [{ type: 'image', url: '/project_images_compressed/animation/toy-plane/toy-plane.jpg' }, { type: 'video', url: 'https://www.youtube.com/watch?v=SLQewRrBwLQ' }],
    },
    {
      id: 'and-they-danced',
      title: 'And They Danced',
      date: '2024',
      summary: 'A vibrant animated piece capturing the joy of dance.',
      images: [
        { type: 'image', url: '/project_images_compressed/animation/and-they-danced/and-they-danced.jpg' },
        { type: 'video', url: 'https://www.youtube.com/watch?v=QajT2onJ4G8' },
      ],
    },
    {
      id: 'plain-dreamin',
      title: 'Plain Dreamin (Credits)',
      date: '2025',
      summary: 'End credits animation for the Award Nominated Short Film about being true to yourself, directed by Alexander Rubenstein.',
      images: [{ type: 'image', url: '/project_images_compressed/animation/plain-dreamin/plain-dreamin1.png' },
               { type: 'video', url: 'https://www.youtube.com/watch?v=jSBwBwZONPk' }],
    },
    {
      id: 'what-a-witch',
      title: 'What a Witch! (Credits)',
      date: '2025',
      summary: 'End credits animation for the amazing slasher short film What a Witch! directed by Tess MacFarlane.',
      images: [{ type: 'image', url: '/project_images_compressed/animation/what-a-witch/what-a-witch1.png' },
               { type: 'video', url: 'https://www.youtube.com/watch?v=DQTt0gEtR24' }],
    },
    {
      id: 'while-weve-still-got-feet',
      title: 'While We’ve Still Got Feet (Credits)',
      date: '2024',
      summary: 'End credits animation for the Award Winning Short Film about innocence and loss, directed by Eliot Kimball.',
      images: [{ type: 'image', url: '/project_images_compressed/design/while-weve-still-got-feet/while-weve-still-got-feet-main.jpg' },
               { type: 'video', url: 'https://www.youtube.com/watch?v=R6fKSMvLIA0' }],
      awards: [
        'Finalist Films Infest NYCIFIF 2024',
        'Official Selection Cannes Indie Shorts Award 2024',
        'Official Selection New York International Film Awards 2024',
        'Official Selection Indie Short Fest 2024',
      ],
    },
    {
      id: 'snowman',
      title: 'Snowman',
      date: '2023',
      summary: 'A heartwarming story of a snowman’s journey.',
      images: [
        { type: 'image', url: '/project_images_compressed/animation/snowman/snowman.jpg' },
        { type: 'video', url: 'https://www.youtube.com/watch?v=74ii7axDhro' },
      ],
    },
  ], []);

  const getSelectedProjectFromQuery = () => {
    const projectId = new URLSearchParams(window.location.search).get('project');
    return projects.find((project) => project.id === projectId) || null;
  };

  const [selectedProject, setSelectedProject] = useState(getSelectedProjectFromQuery);
  const [imageLoaded, setImageLoaded] = useState(new Array(projects.length).fill(false));

  React.useEffect(() => {
    const projectId = new URLSearchParams(window.location.search).get('project');
    setSelectedProject(projects.find((project) => project.id === projectId) || null);
  }, [projects]);

  const handleSelectProject = (project) => {
    const params = new URLSearchParams(window.location.search);
    params.set('project', project.id);
    const nextUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.pushState({}, '', nextUrl);
    setSelectedProject(project);
  };

  const handleBack = () => {
    const nextUrl = window.location.pathname;
    window.history.pushState({}, '', nextUrl);
    setSelectedProject(null);
  };

  const handleImageLoad = (index) => {
    setImageLoaded((prev) => {
      const newLoadedState = [...prev];
      newLoadedState[index] = true;
      return newLoadedState;
    });
  };

  return (
    <AnimatePresence mode="wait">
      {selectedProject ? (
        <motion.div
          key="projectPage"
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -50 }}
          transition={{ duration: 0.5 }}
        >
          <ProjectPage project={selectedProject} onBack={handleBack} />
        </motion.div>
      ) : (
        <motion.div
          key="projectList"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 50 }}
          transition={{ duration: 0.5 }}
        >
          <div className="projects-container">
            {projects.map((project, index) => (
              <div
                key={index}
                className={`project-item ${index % 2 === 0 ? 'left-first' : 'right-first'}`}
                onClick={() => handleSelectProject(project)}
              >
                <div className={`project-image-shell ${imageLoaded[index] ? 'is-loaded' : 'is-loading'}`}>
                  <img
                    src={project.images[0].url}
                    alt={project.title}
                    className="project-image"
                    onLoad={() => handleImageLoad(index)}
                    loading="lazy"
                  />
                </div>
                <div className="project-text">
                  <div className="project-title">
                    <HighlightedTitle text={project.title} />
                    <p>{project.date}</p>
                  </div>
                  <p className="project-summary">{project.summary}</p>
                  {project.awards && (
                    <div className="project-awards">
                      {project.awards.map((award, i) => (
                        <p key={i} className="project-award">
                          {award}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default Animation;